/**
 * 📚 **AI 자료** — 운영자가 올린 글을 도슨트가 근거로 쓰게 보관한다.
 *
 * ## 「학습」이 아니다
 * 모델을 학습시키는 게 아니라, 올린 글을 **조각내 두었다가 질문과 관련된 조각만 찾아**
 * 함께 넣어 주고 「이 안에서만 말해」라고 시킨다(RAG). 운영자에게는 이쪽이 낫다:
 *  · 올리는 즉시 반영된다(재학습 대기 없음)
 *  · **지우면 사라진다**(학습시킨 건 못 지운다)
 *  · 어느 조각에서 나온 답인지 보여 줄 수 있어 **검수가 된다**
 *
 * ## 조각내는 기준은 문단이다
 * 글자 수로 자르면 문장 한가운데가 끊겨 근거로 못 쓴다. 빈 줄로 나뉜 문단이 기본 단위고,
 * 제목 줄은 뒤 내용에 붙이며, 너무 긴 문단은 문장 경계에서만 나눈다(자세히는 chunkText).
 */

/** 이 자료를 어디에 쓸지 — 관리자에서 묶음마다 하나 고른다. */
export const KB_TAGS = ["book", "heritage", "goods", "howto", "guardian"] as const;
export type KbTag = (typeof KB_TAGS)[number];

export const KB_TAG_LABEL: Readonly<Record<KbTag, string>> = {
  book: "📖 책 이야기",
  heritage: "🏛️ 경주 유적",
  goods: "🛍️ 굿즈",
  howto: "❓ 이용 방법",
  guardian: "👨‍👩‍👧 보호자용",
};

export interface KbDoc {
  id: string;
  /** 어디서 온 글인지 — 답의 근거로 이 이름을 보여 준다. */
  title: string;
  text: string;
}

export interface KbPack {
  id: string;
  name: string;
  tag: KbTag;
  docs: KbDoc[];
}

export interface KbPackDoc {
  packs: KbPack[];
}

export const KB_LIMITS = {
  /** 묶음 수 — 이보다 많으면 어느 걸 골랐는지 운영자가 못 센다. */
  packs: 30,
  /** 한 묶음의 조각 수. */
  docs: 300,
  /** 조각 한 개의 글자 수 — 모델에 넣을 때 비용·지연이 여기서 결정된다. */
  chunk: 600,
  /** 이보다 짧고 마침표로 안 끝나면 **제목 줄**로 보고 뒤 내용에 붙인다. */
  minChunk: 40,
  name: 40,
  title: 80,
  /** 한 번에 붙여넣을 수 있는 글 — 넘으면 잘라 안내한다. */
  paste: 200_000,
} as const;

const ID_RE = /^[a-z0-9_-]{1,48}$/;
const str = (v: unknown, max: number): string =>
  typeof v === "string" ? v.trim().slice(0, max) : "";

export function isKbTag(v: unknown): v is KbTag {
  return typeof v === "string" && (KB_TAGS as readonly string[]).includes(v);
}

/** 제목처럼 보이는 줄인가 — **짧고, 문장부호로 끝나지 않는다**. */
function looksLikeHeading(s: string, min: number): boolean {
  return s.length < min && !/[.!?。？！…:：]$/.test(s);
}

/**
 * 긴 글 → 조각들. **문단이 기본 단위**다.
 *
 * - 빈 줄로 문단을 나눈다
 * - **제목 줄은 뒤 내용에 붙인다** — 제목만 있는 조각은 검색에 걸려도 답에 넣을 내용이 없다.
 *   제목인지는 **길이가 아니라 마침표로** 가른다: 한국어 문단은 원래 짧은 것이 많아서
 *   길이로만 보면 멀쩡한 문단 둘이 들러붙는다(그렇게 만들었다가 되돌렸다)
 * - 너무 긴 문단은 **문장 끝에서만** 나눈다(글자 수로 자르면 말이 잘린다)
 */
export function chunkText(
  text: string,
  max: number = KB_LIMITS.chunk,
  min: number = KB_LIMITS.minChunk,
): string[] {
  if (typeof text !== "string") return [];
  const paras = text
    .replace(/\r\n/g, "\n")
    .split(/\n\s*\n+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const out: string[] = [];
  /** 앞에서 만난 제목 줄 — 다음 문단 앞에 얹는다. */
  let heading = "";
  for (const raw of paras) {
    if (looksLikeHeading(raw, min)) {
      // 제목이 연달아 나오면 이어 붙인다(대제목 + 소제목)
      heading = heading ? `${heading}\n${raw}` : raw;
      continue;
    }
    const joined = heading ? `${heading}\n${raw}` : raw;
    const para = joined.length <= max ? joined : raw;
    if (heading && para === raw) out.push(heading); // 붙이면 상한을 넘는다 — 따로 둔다
    heading = "";

    if (para.length <= max) {
      out.push(para);
      continue;
    }
    // 긴 문단: 문장 경계(。.!?…와 줄바꿈)에서만 나눈다
    const sentences = para.split(/(?<=[.!?。？！…])\s+|\n/).filter(Boolean);
    let buf = "";
    for (const sen of sentences) {
      if (buf && buf.length + sen.length + 1 > max) {
        out.push(buf);
        buf = sen;
      } else {
        buf = buf ? `${buf} ${sen}` : sen;
      }
      // 문장 하나가 상한보다 길면 그때만 글자 수로 나눈다(다른 방법이 없다)
      while (buf.length > max) {
        out.push(buf.slice(0, max));
        buf = buf.slice(max);
      }
    }
    if (buf) out.push(buf);
  }
  if (heading) out.push(heading); // 마지막이 제목뿐이면 그거라도 남긴다
  return out;
}

/** 붙여넣은 글 한 덩이 → 조각 문서들. `title` 은 근거 표시에 쓰인다. */
export function docsFromText(title: string, text: string, idPrefix: string): KbDoc[] {
  const name = str(title, KB_LIMITS.title) || "제목 없음";
  return chunkText(str(text, KB_LIMITS.paste)).map((t, i) => ({
    id: `${idPrefix}_${i}`,
    title: name,
    text: t,
  }));
}

function normalizeKbDocEntry(input: unknown, i: number): KbDoc | null {
  const o = (input ?? {}) as Record<string, unknown>;
  const text = str(o.text, KB_LIMITS.chunk);
  if (!text) return null;
  return {
    id: typeof o.id === "string" && ID_RE.test(o.id) ? o.id : `d${i}`,
    title: str(o.title, KB_LIMITS.title) || "제목 없음",
    text,
  };
}

/** 밖에서 온 문서를 믿을 수 있는 모양으로. 깨진 항목은 버리되 나머지는 살린다. */
export function normalizeKbPackDoc(input: unknown): KbPackDoc {
  const o = (input ?? {}) as Record<string, unknown>;
  const raw = Array.isArray(o.packs) ? o.packs : [];
  const seen = new Set<string>();
  const packs: KbPack[] = [];
  for (const item of raw) {
    const p = (item ?? {}) as Record<string, unknown>;
    const id = typeof p.id === "string" && ID_RE.test(p.id) ? p.id : "";
    if (!id || seen.has(id)) continue;
    seen.add(id);
    packs.push({
      id,
      name: str(p.name, KB_LIMITS.name) || id,
      tag: isKbTag(p.tag) ? p.tag : "howto",
      docs: (Array.isArray(p.docs) ? p.docs : [])
        .map((d, i) => normalizeKbDocEntry(d, i))
        .filter((d): d is KbDoc => d !== null)
        .slice(0, KB_LIMITS.docs),
    });
    if (packs.length >= KB_LIMITS.packs) break;
  }
  return { packs };
}

/** 고른 묶음들의 조각을 한 줄로 — 도슨트 근거 검색(`retrieveChunks`)에 그대로 넣는다. */
export function chunksOf(doc: KbPackDoc, packIds: readonly string[]): KbDoc[] {
  const want = new Set(packIds);
  return doc.packs.filter((p) => want.has(p.id)).flatMap((p) => p.docs);
}

/** 묶음 요약(관리자 목록·출시 준비) — 조각이 0개면 「아직 비었다」를 말할 수 있어야 한다. */
export function packSummary(doc: KbPackDoc): { id: string; name: string; tag: KbTag; docs: number }[] {
  return doc.packs.map((p) => ({ id: p.id, name: p.name, tag: p.tag, docs: p.docs.length }));
}

/** 다음 묶음 id — 안 쓰는 가장 작은 번호(지운 자리를 다시 쓴다). */
export function nextPackId(doc: KbPackDoc): string {
  const taken = new Set(doc.packs.map((p) => p.id));
  for (let n = 1; n <= 999; n++) if (!taken.has(`pack_${n}`)) return `pack_${n}`;
  throw new Error("자료 묶음을 더 만들 수 없어요(999개)");
}
